// Left and right labels of the call-to-action banner, per phase:
// "in-person" (VIZBI / 2026), "online" (VIZBI / <i>Online</i>) or "library" (nothing upcoming).
// A line is a string, {text, isItalic}, or a flag before its dates: {flag: "🇦🇺", text: "Sydney
// local time", suffix: "Nov 4, 19 & 20"}.

// A month name is shortened on narrow screens; dates such as "Nov 3, 18 & 19" are already short
function getMonth(monthName, displayWidth) {
  if (!/^[A-Za-z]+$/.test(monthName)) return monthName;
  if (["narrow", "medium"].includes(displayWidth)) return monthName.slice(0, 3);
  return monthName;
}

// the viewer's flag before their dates, always on the dates' line (Seán, 2026-10-22, issue BN2)
const getFlagged = (eventNote, text) => ({...eventNote, suffix: text});
// "Nov 4, 19 & 20" → "Nov 4, 19, 20": tighter, and with a flag always (Seán, 2026-10-23)
const getCompact = (dates) => dates.replace(" & ", ", ");

// The right label stacked (where one line doesn't fit, useBannerFit.js): month (or dates) over
// the year, or with the viewer's flag before them when they're moved to their calendar
// (eventNote: the flag, or "Local time" for a zone without one; useLocalEventDates.js,
// zoneCountries.js). On narrow screens the dates are split over two lines without the year:
// "Nov 4," / "19 & 20", the flag before the first
function getEventLines(eventMonth, year, displayWidth, eventNote) {
  const commaIndex = eventMonth.indexOf(", ");
  const isSplit = displayWidth === "narrow" && commaIndex !== -1;
  const month = getMonth(eventMonth, displayWidth);
  if (eventNote?.flag && !isSplit) return [getFlagged(eventNote, getCompact(month))];
  const under = displayWidth === "narrow" ? String(year) : eventNote || String(year);
  if (!isSplit) return [month, under];
  const first = eventMonth.slice(0, commaIndex + 1);
  if (!eventNote?.flag) return [first, eventMonth.slice(commaIndex + 2)];
  return [getFlagged(eventNote, first), getCompact(eventMonth).slice(commaIndex + 2)];
}

function getTotalsLines(totals, isStacked) {
  if (!totals) return [];
  const [hours, videos] = [`${totals.hours} hours`, `${totals.videos} videos`];
  return isStacked ? [hours, videos] : [`${hours}, ${videos}`];
}

function getLibraryLabels(totals, displayWidth) {
  const isStacked = displayWidth === "narrow";
  const left = isStacked ? ["VIZBI", "Library"] : ["VIZBI Library"];
  return {to: "/Videos/Unlock", isStacked, left, right: getTotalsLines(totals, isStacked)};
}

// the labels on one line (useBannerFit.js): "Nov 3, 18 & 19, 2026", without the year, then
// tighter: "Nov 3, 18, 19" (Seán, 2026-10-22, issue BN1); with the dates moved to the viewer's
// calendar, their flag before them, never dropped: "🇦🇺 Nov 4, 19 & 20"; a zone without a flag,
// "(local time)" after them
function getInlineRight(eventMonth, year, eventNote) {
  if (!eventMonth) return {right: [], rightShort: [], rightCompact: []};
  const compact = getCompact(eventMonth);
  if (eventNote?.flag) {
    const flagged = [getFlagged(eventNote, compact)]; // with a flag, always "Nov 4, 19, 20"
    return {right: flagged, rightShort: flagged, rightCompact: flagged};
  }
  const under = eventNote ? `(${eventNote.toLowerCase()})` : String(year);
  const first = /\d/.test(eventMonth) && !eventNote ? `${eventMonth},` : eventMonth;
  return {right: [first, under], rightShort: [eventMonth], rightCompact: [compact]};
}

// eventMonth: full English month name of the upcoming event (e.g. "November"), or its dates
// (e.g. "Nov 3, 18 & 19", from keyDates.json bannerDates)
export function getBannerLabels({phase, year, eventMonth, eventNote, totals, displayWidth}) {
  if (phase === "library") return getLibraryLabels(totals, displayWidth);
  const online = {text: "Online", isItalic: true};
  const left = phase === "online" ? ["VIZBI", online] : ["VIZBI", String(year)];
  const right = eventMonth ? getEventLines(eventMonth, year, displayWidth, eventNote) : [];
  const inline = {left, ...getInlineRight(eventMonth, year, eventNote)};
  return {to: `/${year}`, isStacked: true, left, right, inline};
}
